import { useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Download, Play, Plus, Trash2 } from 'lucide-react';

interface ReportColumn {
  name: string;
  source: string;
}

interface ReportFilter {
  column: string;
  operator: string;
  value: string;
}

interface ReportQuery {
  entityType: string;
  columns: ReportColumn[];
  filters: ReportFilter[];
  limit: number;
}

interface ReportResult {
  columns: string[];
  rows: (string | null)[][];
  error?: string | null;
}

const ENTITIES = ['ACCOUNT', 'OPPORTUNITY', 'LEAD', 'CONTACT'] as const;

const ENTITY_FIELDS: Record<string, string[]> = {
  ACCOUNT: ['id', 'name', 'status', 'industry', 'created_at', 'updated_at'],
  OPPORTUNITY: ['id', 'name', 'status', 'estimated_deal_value', 'forecast_classification', 'created_at', 'updated_at'],
  LEAD: ['id', 'name', 'status', 'email', 'source', 'created_at', 'updated_at'],
  CONTACT: ['id', 'first_name', 'last_name', 'email', 'title', 'created_at', 'updated_at'],
};

const OPERATORS = ['eq', 'ne', 'gt', 'lt', 'like', 'in'] as const;

export function ReportBuilderPage() {
  const [entityType, setEntityType] = useState<string>(ENTITIES[0]);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set(['name', 'status', 'created_at']));
  const [filters, setFilters] = useState<ReportFilter[]>([]);
  const [orderBy, setOrderBy] = useState<string>('');
  const [limit, setLimit] = useState(100);
  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availableFields = ENTITY_FIELDS[entityType] ?? [];

  function handleEntityChange(newEntity: string) {
    setEntityType(newEntity);
    setSelectedFields(new Set(['name', 'status', 'created_at']));
    setFilters([]);
    setOrderBy('');
    setResult(null);
  }

  function toggleField(field: string) {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  }

  function addFilter() {
    setFilters((prev) => [...prev, { column: availableFields[0] ?? 'name', operator: 'eq', value: '' }]);
  }

  function removeFilter(index: number) {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  }

  function updateFilter(index: number, patch: Partial<ReportFilter>) {
    setFilters((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  }

  async function runQuery() {
    if (selectedFields.size === 0) {
      setError('Select at least one field');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const query: ReportQuery = {
        entityType,
        columns: [...selectedFields].map((name) => ({ name, source: 'core' })),
        filters: filters.filter((f) => f.value.trim() !== ''),
        limit,
      };
      const { data } = await apiClient.post<ReportResult>('/analytics/report-query', query);
      if (data.error) {
        setError(data.error);
      } else {
        setResult(data);
      }
    } catch {
      setError('Failed to run report query');
    } finally {
      setLoading(false);
    }
  }

  function exportCsv() {
    if (!result || result.rows.length === 0) return;
    const header = result.columns.join(',');
    const rows = result.rows
      .map((row) =>
        row
          .map((cell) => {
            const val = cell ?? '';
            return val.includes(',') ? `"${val}"` : val;
          })
          .join(','),
      )
      .join('\n');
    const blob = new Blob([header + '\n' + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `report-${entityType.toLowerCase()}-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Report Builder</h1>

      {/* Entity selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Entity</label>
        <select
          value={entityType}
          onChange={(e) => handleEntityChange(e.target.value)}
          className="flex h-10 w-full max-w-xs rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          {ENTITIES.map((e) => (
            <option key={e} value={e}>
              {e.charAt(0) + e.slice(1).toLowerCase()}s
            </option>
          ))}
        </select>
      </div>

      {/* Fields */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Fields</label>
        <div className="flex flex-wrap gap-3">
          {availableFields.map((field) => (
            <label key={field} className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={selectedFields.has(field)}
                onChange={() => toggleField(field)}
                className="accent-primary"
              />
              {field}
            </label>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Filters</label>
          <Button size="sm" variant="outline" onClick={addFilter} className="h-7 text-xs">
            <Plus className="h-3 w-3" />
            Add filter
          </Button>
        </div>
        {filters.map((filter, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <select
              value={filter.column}
              onChange={(e) => updateFilter(idx, { column: e.target.value })}
              className="flex h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {availableFields.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            <select
              value={filter.operator}
              onChange={(e) => updateFilter(idx, { operator: e.target.value })}
              className="flex h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {OPERATORS.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
            <Input
              value={filter.value}
              onChange={(e) => updateFilter(idx, { value: e.target.value })}
              placeholder="Value"
              className="h-9 max-w-xs"
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => removeFilter(idx)}
              className="h-9 w-9 p-0"
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        ))}
      </div>

      {/* Order by & Limit */}
      <div className="flex flex-wrap gap-4">
        <div className="space-y-1">
          <label className="text-sm font-medium">Order by</label>
          <select
            value={orderBy}
            onChange={(e) => setOrderBy(e.target.value)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">-- none --</option>
            {availableFields.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Limit</label>
          <Input
            type="number"
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value) || 100)}
            className="h-10 w-28"
            min={1}
            max={1000}
          />
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <Button onClick={runQuery} disabled={loading || selectedFields.size === 0}>
          <Play className="h-4 w-4" />
          {loading ? 'Running...' : 'Run Query'}
        </Button>
        {result && result.rows.length > 0 && (
          <Button variant="outline" onClick={exportCsv}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Results table */}
      {result && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {result.rows.length} row{result.rows.length !== 1 ? 's' : ''} returned
          </p>
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                <tr>
                  {result.columns.map((col) => (
                    <th key={col} className="whitespace-nowrap px-3 py-2">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row, ri) => (
                  <tr key={ri} className="border-t">
                    {row.map((cell, ci) => (
                      <td key={ci} className="whitespace-nowrap px-3 py-2">
                        {cell ?? '---'}
                      </td>
                    ))}
                  </tr>
                ))}
                {result.rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={result.columns.length}
                      className="px-3 py-6 text-center text-muted-foreground"
                    >
                      No results
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
